/**
 * Display formatting for meeting-type preferences, shared so the settings
 * lists on web and native summarize a meeting type identically
 * ("30 min · Virtual · M, Tu, W, Th, F").
 *
 * Typed structurally over the availability/preference shapes rather than the
 * API types so both apps' row models pass unchanged.
 */
const ALL_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const ALL_DAY_LABELS: Record<(typeof ALL_DAY_KEYS)[number], string> = {
  sun: "Su",
  mon: "M",
  tue: "Tu",
  wed: "W",
  thu: "Th",
  fri: "F",
  sat: "Sa",
};

/** Summarize which days have availability set, e.g. "M, Tu, W, Th, F" */
export function summarizeAvailabilityDays(availability: Record<string, unknown>): string | null {
  const days = ALL_DAY_KEYS.filter((d) => {
    const slots = availability[d];
    return Array.isArray(slots) && slots.length > 0;
  });
  if (days.length === 0) return null;
  return days.map((d) => ALL_DAY_LABELS[d]).join(", ");
}

export const formatMeetingDuration = (mins: number): string => {
  if (mins < 60) return `${mins} min`;
  if (mins === 60) return "1 hour";
  if (mins % 60 === 0) return `${mins / 60} hours`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

/** "duration · Virtual/In-person · active days", skipping whatever is unset. */
export const summarizeMeetingPreference = (pref: {
  typicalDuration?: number | null;
  isVirtual?: boolean | null;
  availability?: Record<string, unknown> | null;
}): string => {
  const parts: string[] = [];
  if (pref.typicalDuration) {
    parts.push(formatMeetingDuration(pref.typicalDuration));
  }
  if (pref.isVirtual != null) {
    parts.push(pref.isVirtual ? "Virtual" : "In-person");
  }
  const days = summarizeAvailabilityDays(pref.availability ?? {});
  if (days) parts.push(days);
  return parts.join(" · ");
};
